import React, { useCallback, useEffect, useState } from 'react'
import { Form, Input, Button, Skeleton, message, Popconfirm, Select, Result, Modal } from 'antd'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import B2BLayout from '../../../layouts/b2b'
import { HStack } from '../../../ui/Stack'
import { PlusOutlined, MinusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import './style.css'
import { ApiResponse, Correction } from './interrfaces'
import { ResultStatusType } from 'antd/es/result'

interface IError {
  statusCode: ResultStatusType
  message: string
}

export const ClaimStagePlanEditor = () => {
  const [loading, setLoading] = useState(true)
  const [accessError, setAccessError] = useState<IError | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [form] = Form.useForm()
  const [data, setData] = useState<ApiResponse>({ settings: [], stages: [], productTypes: [] })
  const [correctionInputs, setCorrectionInputs] = useState<
    Record<number, { typeId: number | null; days: number | null }>
  >({})
  const [showAddCorrectionForm, setShowAddCorrectionForm] = useState<Record<number, boolean>>({})
  const [editingCorrection, setEditingCorrection] = useState<Record<number, number | null>>({})
  const [helpModalVisible, setHelpModalVisible] = useState(false)
  const [worksHelpModalVisible, setWorksHelpModalVisible] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = (await fetchAPI('/api/admin/claim-stage-plan')) as ApiResponse
        setData(response)
        setAccessError(null)
      } catch (err) {
        const statusCode = ((err as IHttpError)?.statusCode || 'error') as ResultStatusType
        const message = (err as IHttpError)?.message || 'Неизвестная ошибка'
        setAccessError({ message, statusCode })
        console.error(`Ошибка загрузки данных: ${message}`)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleSave = useCallback(async () => {
    const values = form.getFieldsValue()
    if (values.length === 0) return message.warning('Пустой список настроек')
    const settingsWithCorrection = values.settings.map((setting: any, index: number) => ({
      ...setting,
      correction: data.settings[index]?.correction || [],
    }))
    try {
      setLoading(true)
      setSaveError(null)
      await fetchAPI('/api/admin/claim-stage-plan', {
        method: 'POST',
        body: JSON.stringify({ settings: settingsWithCorrection }),
      })
      message.success('Данные успешно сохранены')
      setData(prevData => ({ ...prevData, settings: settingsWithCorrection }))
      form.setFieldsValue({ settings: settingsWithCorrection })
    } catch (err) {
      const errorMessage = (err as IHttpError)?.message || 'Неизвестная ошибка при сохранении'
      setSaveError(errorMessage)
      message.error(`Ошибка сохранения данных: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }, [form, data])

  const handleAdd = () => {
    const newSettings = [
      ...data.settings,
      { name: '', stage: 0, min_days: 0, max_days: 0, correction: [] },
    ]
    setData({ ...data, settings: newSettings })
  }

  const handleRemove = useCallback(
    (index: number) => {
      const newSettings = data.settings.filter((_, i) => i !== index)
      setData(prevData => ({ ...prevData, settings: [...newSettings] }))
    },
    [data],
  )

  const handleAddCorrection = useCallback(
    (settingIndex: number) => {
      const input = correctionInputs[settingIndex]
      if (!input?.typeId || input.days === null || input.days === undefined) {
        return message.warning('Заполните тип изделия и количество дней')
      }

      const setting = data.settings[settingIndex]
      const editingIndex = editingCorrection[settingIndex]

      if (editingIndex !== null && editingIndex !== undefined) {
        // Редактирование существующей коррекции
        const maxCorrection = setting.max_days - setting.min_days
        if (input.days < 0 && Math.abs(input.days) > maxCorrection) {
          return message.warning(`Корректировка не может быть больше ${maxCorrection} дней`)
        }

        const newSettings = [...data.settings]
        newSettings[settingIndex] = {
          ...setting,
          correction:
            setting.correction?.map((c, i) =>
              i === editingIndex ? { type_id: input.typeId!, days: input.days! } : c,
            ) || [],
        }
        setData(prevData => ({ ...prevData, settings: newSettings }))
        setCorrectionInputs(prev => ({ ...prev, [settingIndex]: { typeId: null, days: null } }))
        setEditingCorrection(prev => ({ ...prev, [settingIndex]: null }))
        setShowAddCorrectionForm(prev => ({ ...prev, [settingIndex]: false }))
      } else {
        // Добавление новой коррекции
        const existingCorrection = setting.correction?.find(c => c.type_id === input.typeId)
        if (existingCorrection) {
          return message.warning('Корректировка для этого типа изделия уже добавлена')
        }

        const maxCorrection = setting.max_days - setting.min_days
        if (input.days < 0 && Math.abs(input.days) > maxCorrection) {
          return message.warning(`Корректировка не может быть больше ${maxCorrection} дней`)
        }

        const newCorrection: Correction = { type_id: input.typeId, days: input.days }
        const newSettings = [...data.settings]
        newSettings[settingIndex] = {
          ...setting,
          correction: [...(setting.correction || []), newCorrection],
        }
        setData(prevData => ({ ...prevData, settings: newSettings }))
        setCorrectionInputs(prev => ({ ...prev, [settingIndex]: { typeId: null, days: null } }))
        setShowAddCorrectionForm(prev => ({ ...prev, [settingIndex]: false }))
      }
    },
    [data, correctionInputs, editingCorrection],
  )

  const handleRemoveCorrection = useCallback(
    (settingIndex: number, correctionIndex: number) => {
      const newSettings = [...data.settings]
      const setting = { ...newSettings[settingIndex] }
      setting.correction = setting.correction?.filter((_, i) => i !== correctionIndex) || []
      newSettings[settingIndex] = setting
      setData(prevData => ({ ...prevData, settings: newSettings }))
    },
    [data],
  )

  const handleEditCorrection = useCallback(
    (settingIndex: number, correctionIndex: number) => {
      const correction = data.settings[settingIndex]?.correction?.[correctionIndex]
      if (correction) {
        setCorrectionInputs(prev => ({
          ...prev,
          [settingIndex]: { typeId: correction.type_id, days: correction.days },
        }))
        setEditingCorrection(prev => ({ ...prev, [settingIndex]: correctionIndex }))
        setShowAddCorrectionForm(prev => ({ ...prev, [settingIndex]: true }))
      }
    },
    [data],
  )

  /**
   * Хлебные крошки
   */
  const breadcrumbs = [
    {
      href: '/admin',
      title: 'Админ панель',
    },
    {
      href: '',
      title: 'Настройки плана исполнения рекламации',
    },
  ]

  const formItemLayout = {
    labelCol: {
      sm: { span: 6 },
    },
    wrapperCol: {
      sm: { span: 18 },
    },
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active />}
      {!loading && accessError && (
        <Result
          status={accessError.statusCode}
          title="Ошибка доступа"
          subTitle={accessError.message}
        />
      )}
      {!loading && !accessError && (
        <Form form={form} initialValues={data} layout="horizontal" {...formItemLayout}>
          {data?.settings?.length > 0 &&
            data.settings.map((_setting, index) => (
              <div
                key={index}
                style={{
                  marginBottom: 24,
                  padding: 16,
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                }}
              >
                <Form.Item
                  label="Название"
                  name={['settings', index, 'name']}
                  rules={[{ required: true, message: 'Введите название' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Стадия"
                  name={['settings', index, 'stage']}
                  rules={[{ required: true, message: 'Введите стадию' }]}
                >
                  <Select
                    options={data?.stages.map(stage => ({ label: stage.name, value: stage.code }))}
                  />
                </Form.Item>
                <Form.Item
                  label="Минимальное кол-во дней"
                  name={['settings', index, 'min_days']}
                  rules={[{ required: true, message: 'Введите минимальное количество дней' }]}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  label="Максимальное кол-во дней"
                  name={['settings', index, 'max_days']}
                  rules={[{ required: true, message: 'Введите максимальное количество дней' }]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  label={
                    <div>
                      <span>Корректировка при наличии работ по рекламациям</span>
                      <Button
                        type="link"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setWorksHelpModalVisible(true)}
                        style={{ color: '#1890ff', padding: 0 }}
                      />
                    </div>
                  }
                  name={['settings', index, 'works_correction']}
                >
                  <Input type="number" placeholder="Оставьте пустым для отключения" />
                </Form.Item>

                <Form.Item
                  label={
                    <div>
                      <span>Корректировка по типу изделия</span>
                      <Button
                        type="link"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setHelpModalVisible(true)}
                        style={{ color: '#1890ff', padding: 0 }}
                      />
                    </div>
                  }
                >
                  {data.settings[index]?.correction?.map((correction, correctionIndex) => {
                    const productType = data.productTypes.find(pt => pt.id === correction.type_id)
                    return (
                      <div
                        key={correctionIndex}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 8,
                          marginTop: 5,
                        }}
                      >
                        <span>{productType?.value || `Тип ${correction.type_id}`}:</span>
                        <span style={{ fontWeight: 500 }}>
                          {correction.days > 0 ? `+${correction.days}` : correction.days} дней
                        </span>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleEditCorrection(index, correctionIndex)}
                        >
                          Редактировать
                        </Button>
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={() => handleRemoveCorrection(index, correctionIndex)}
                        >
                          Удалить
                        </Button>
                      </div>
                    )
                  })}

                  {!showAddCorrectionForm[index] && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setCorrectionInputs(prev => ({
                          ...prev,
                          [index]: { typeId: null, days: null },
                        }))
                        setEditingCorrection(prev => ({ ...prev, [index]: null }))
                        setShowAddCorrectionForm(prev => ({ ...prev, [index]: true }))
                      }}
                    >
                      Добавить
                    </Button>
                  )}

                  {showAddCorrectionForm[index] && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Select
                        placeholder="Выберите тип изделия"
                        style={{ flex: 1 }}
                        value={correctionInputs[index]?.typeId || undefined}
                        options={data.productTypes
                          .filter(
                            pt =>
                              !data.settings[index]?.correction?.some(c => c.type_id === pt.id) ||
                              editingCorrection[index] !== null,
                          )
                          .map(pt => ({ label: pt.value, value: pt.id }))}
                        onChange={(value: number) => {
                          setCorrectionInputs(prev => ({
                            ...prev,
                            [index]: { ...prev[index], typeId: value },
                          }))
                        }}
                      />
                      <Input
                        type="number"
                        placeholder="Дни"
                        style={{ width: 100 }}
                        value={correctionInputs[index]?.days ?? undefined}
                        onChange={e => {
                          setCorrectionInputs(prev => ({
                            ...prev,
                            [index]: { ...prev[index], days: Number(e.target.value) },
                          }))
                        }}
                      />
                      <Button
                        type="dashed"
                        icon={editingCorrection[index] !== null ? null : <PlusOutlined />}
                        onClick={() => handleAddCorrection(index)}
                      >
                        {editingCorrection[index] !== null ? 'Сохранить' : 'Добавить'}
                      </Button>
                      <Button
                        type="text"
                        onClick={() => {
                          setShowAddCorrectionForm(prev => ({ ...prev, [index]: false }))
                          setEditingCorrection(prev => ({ ...prev, [index]: null }))
                          setCorrectionInputs(prev => ({
                            ...prev,
                            [index]: { typeId: null, days: null },
                          }))
                        }}
                      >
                        Отмена
                      </Button>
                    </div>
                  )}
                </Form.Item>

                <HStack justify="end">
                  <Popconfirm
                    title="Вы уверены, что хотите удалить этот блок?"
                    onConfirm={() => handleRemove(index)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button danger>Удалить</Button>
                  </Popconfirm>
                </HStack>
              </div>
            ))}

          <Form.Item wrapperCol={{ ...formItemLayout.wrapperCol, offset: 6 }}>
            <Button type="dashed" onClick={handleAdd} style={{ width: '100%', marginBottom: 16 }}>
              Добавить блок
            </Button>
          </Form.Item>
          <Button type="primary" onClick={handleSave} disabled={loading}>
            Сохранить
          </Button>
          {saveError && (
            <div style={{ marginTop: 16, color: '#ff4d4f' }}>Ошибка сохранения: {saveError}</div>
          )}
        </Form>
      )}
      <Modal
        title="Корректировка плана исполнения"
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHelpModalVisible(false)}>
            Закрыть
          </Button>,
        ]}
      >
        <div>
          <div>• Корректировка влияет только на максимальное количество дней</div>
          <div>• Положительное значение увеличивает, отрицательное уменьшает</div>
          <div>• Отрицательная коррекция не может быть больше максимального количества дней</div>
        </div>
      </Modal>

      <Modal
        title="Корректировка при наличии работ по рекламациям"
        open={worksHelpModalVisible}
        onCancel={() => setWorksHelpModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setWorksHelpModalVisible(false)}>
            Закрыть
          </Button>,
        ]}
      >
        <div>
          <div>• Корректировка применяется, если для рекламации создана работа</div>
          <div>• Положительное значение увеличивает, отрицательное уменьшает</div>
          <div>• Оставьте поле пустым, чтобы отключить корректировку</div>
        </div>
      </Modal>
    </B2BLayout>
  )
}
